import { computed, inject, Injectable, signal } from "@angular/core";
import { OKTA_AUTH } from "@okta/okta-angular";
import type { UserClaims } from "@okta/okta-auth-js";
import { catchError, from, Observable, of } from "rxjs";

@Injectable({ providedIn: "root" })
export class AuthService {
  /**
   * True once Okta holds a valid session, i.e. once the auth token interceptor can attach an
   * access token. Backend calls that must not go out unauthenticated (the gateway answers 401)
   * wait for this instead of firing on store init.
   */
  readonly isAuthenticated = computed(() => this.authenticated());

  private readonly oktaAuth = inject(OKTA_AUTH);
  private readonly authenticated = signal(
    this.oktaAuth.authStateManager.getAuthState()?.isAuthenticated ?? false,
  );

  constructor() {
    this.oktaAuth.authStateManager.subscribe((authState) =>
      this.authenticated.set(authState.isAuthenticated ?? false),
    );
  }

  getProfile(): Observable<UserClaims | null> {
    return from(this.oktaAuth.getUser()).pipe(
      catchError(() => {
        const { idToken } = this.oktaAuth.tokenManager.getTokensSync();
        return of((idToken?.claims as UserClaims | undefined) ?? null);
      }),
    );
  }

  async logout(): Promise<void> {
    await this.oktaAuth.signOut();
  }
}
